import React, { useEffect, useState } from 'react';
import './AddNewCompanyAddress.scss';
import { useDispatch } from 'react-redux';
import ApiService from '../../services/apiService';
import URLS from '../../urls/Urls';
import { toast } from 'react-toastify';
import { toastOptions } from '../../utils/toast';
import Filter from '../oms/Options/Filter/Filter';
import { FilterOptionSchema } from '../../redux/oms/omsTypes';
import { ALL_COUNTRIES } from '../oms/NewAddressForm/AllCountries.data';
import { ADDRESS_STATUS, BILLING_TYPE } from './AddNewCompany.data';

interface CompanyAddressProps {
  companyId: number | null;
  addressType: string;
  handleClose: () => void;
  update?: boolean;
  editingData?: {
    id: number;
    address_line: string;
    address_type: string;
    city: string;
    country: string;
    pincode: string;
    state: string;
    poc_id?: number;
    poc_name?: string;
    poc_number?: string;
    active: boolean;
    gstin: string;
    map_link: string;
  };
  handleAddressUpdate: (
    address_id: number,
    addressType: string,
    updatedData: any
  ) => void;
  handleNewAddressAdd: (adress_data: any) => void;
}

const AddNewCompanyAddress: React.FC<CompanyAddressProps> = ({
  companyId,
  addressType,
  handleClose,
  update,
  editingData,
  handleAddressUpdate,
  handleNewAddressAdd,
}) => {
  const [addressLine, setAddressLine] = useState(
    editingData?.address_line || ''
  );
  const [city, setCity] = useState(editingData?.city || '');
  const [gstin, setGstin] = useState(editingData?.gstin || '');
  const [mapLink, setMapLink] = useState(editingData?.map_link || '');
  const [poc, setPoc] = useState(editingData?.poc_name || '');
  const [pocNumber, setPocNumber] = useState(editingData?.poc_number || '');
  const [state, setState] = useState(editingData?.state || '');
  const [pincode, setPincode] = useState(editingData?.pincode || '');
  const [selectedCountry, setSelectedCountry] = useState<FilterOptionSchema[]>([
    ALL_COUNTRIES.find((country) => country.name === editingData?.country) ||
      ALL_COUNTRIES[0],
  ]);
  const [selectedType, setSelectedType] = useState<FilterOptionSchema[]>(
    addressType == 'BILLING' ? [BILLING_TYPE[1]] : [BILLING_TYPE[0]]
  );
  const [selectedStatus, setSelectedStatus] = useState<FilterOptionSchema[]>(
    editingData && editingData?.active
      ? [ADDRESS_STATUS[0]]
      : [ADDRESS_STATUS[1]]
  );

  const validateAddressFields = ({
    poc,
    pocNumber,
    addressLine,
    city,
    state,
    pincode,
    selectedCountry,
    selectedType,
    selectedStatus,
    toastOptions,
    gstin,
    map_link,
  }: {
    poc: string;
    pocNumber: string;
    addressLine: string;
    city: string;
    state: string;
    pincode: string;
    selectedCountry: FilterOptionSchema[];
    selectedType: FilterOptionSchema[];
    selectedStatus: FilterOptionSchema[];
    toastOptions: any;
    gstin: string;
    map_link: string;
  }): boolean => {
    const fields = [
      { value: poc, message: 'Add POC' },
      { value: pocNumber, message: 'Add POC Number' },
      { value: addressLine, message: 'Add Address Line' },
      { value: city, message: 'Add City' },
      { value: state, message: 'Add State' },
      { value: pincode, message: 'Add Pincode' },
      { value: selectedCountry, message: 'Add Country' },
      { value: selectedType, message: 'Add Type' },
      { value: selectedStatus, message: 'Add Status' },
      // { value: gstin, message: 'Add GSTIN' },
      // { value: mapLink, message: 'Add Map Link' },
    ];

    for (const field of fields) {
      if (!field.value || field.value.length <= 0) {
        toast.error(field.message, toastOptions);
        return false;
      }
    }
    return true;
  };

  const handleAddAddress = async () => {
    const isValid = validateAddressFields({
      poc,
      pocNumber,
      addressLine,
      city,
      state,
      pincode,
      selectedCountry,
      selectedType,
      selectedStatus,
      toastOptions,
      gstin,
      map_link: mapLink,
    });

    if (!isValid) return;

    const formData = new FormData();
    const addressDetails = {
      name: poc,
      contact_number: pocNumber,
      address_line: addressLine,
      city,
      state,
      pincode,
      country: selectedCountry[0]?.name,
      address_type: addressType.toUpperCase(),
      map_link: mapLink,
      gstin,
    };

    formData.append('company_id', companyId?.toString() || '');
    formData.append('address_details', JSON.stringify(addressDetails));

    handleNewAddressAdd(formData);
    handleClose();
  };

  const handleUpdateAddress = async () => {
    const isValid = validateAddressFields({
      poc,
      pocNumber,
      addressLine,
      city,
      state,
      pincode,
      selectedCountry,
      selectedType,
      selectedStatus,
      toastOptions,
      gstin,
      map_link: mapLink,
    });

    if (!isValid) return;

    const params: any = {};

    if (pincode !== editingData?.pincode) params.pincode = pincode;
    if (gstin !== editingData?.gstin) params.gstin = gstin;
    if (mapLink !== editingData?.map_link) params.map_link = mapLink;
    if (addressLine !== editingData?.address_line)
      params.address_line = addressLine;
    if (city !== editingData?.city) params.city = city;
    if (state !== editingData?.state) params.state = state;

    if (poc !== editingData?.poc_name) {
      params.poc_id = editingData?.poc_id;
      params.pocName = poc;
    }

    if (pocNumber !== editingData?.poc_number) {
      params.poc_id = editingData?.poc_id;
      params.pocNumber = pocNumber;
    }

    if (selectedCountry[0]?.name !== editingData?.country) {
      params.country = selectedCountry[0].name;
    }

    if (selectedType[0]?.code !== addressType) {
      params.address_type = selectedType[0].code;
    }

    const newActive = selectedStatus[0]?.code === 'ACTIVE';
    if (editingData?.active !== newActive) {
      params.active = newActive;
    }

    if (Object.keys(params).length === 0) {
      toast.info('No changes to save', toastOptions);
      return;
    }

    handleAddressUpdate(editingData?.id || -1, addressType, params);
  };

  return (
    <div className="new-company-add-modal border-box px-16 py-12">
      <div className=" flex-column gap-12 p-16">
        <p style={{ color: '#5151EC' }} className="xetgo-font-button bold ">
          {update ? 'Update' : 'Add'}{' '}
          {addressType.charAt(0).toUpperCase() +
            addressType.slice(1).toLowerCase()}{' '}
          Address
        </p>
      </div>
      <form className="flex-column gap-16 p-16 xetgo-font-tag bold">
        <div className="flex-row gap-12">
          <div className="flex-column gap-8 flex-1">
            <p style={{ color: '#484848' }} className="xetgo-font-tag">
              POC
            </p>
            <input
              type="text"
              className="company-add-billing-input px-8 py-4 xetgo-font-tag"
              placeholder="Enter POC Name "
              onChange={(e) => setPoc(e.target.value)}
              value={poc}
              required
            />
          </div>
          <div className="flex-column gap-8 flex-1">
            <p style={{ color: '#484848' }} className="xetgo-font-tag">
              POC Number
            </p>
            <input
              type="text"
              className="company-add-billing-input px-8 py-4 xetgo-font-tag"
              placeholder="Enter POC Number "
              onChange={(e) => setPocNumber(e.target.value)}
              value={pocNumber}
              required
            />
          </div>
        </div>
        <div className="flex-row gap-12">
          <div className="flex-column gap-8 flex-1">
            <p style={{ color: '#484848' }} className="xetgo-font-tag">
              Address Line
            </p>
            <input
              className="company-add-billing-input px-8 py-4"
              type="text"
              placeholder="Address"
              onChange={(e) => setAddressLine(e.target.value)}
              value={addressLine}
              required
            />
          </div>
        </div>

        <div className="flex-row gap-12">
          <div className="flex-column gap-8 flex-1">
            <p style={{ color: '#484848' }} className="xetgo-font-tag">
              City
            </p>
            <input
              className="company-add-billing-input px-8 py-4"
              type="text"
              placeholder="City  "
              onChange={(e) => setCity(e.target.value)}
              value={city}
              required
            />
          </div>

          <div className="flex-column gap-8 flex-1">
            <p style={{ color: '#484848' }} className="xetgo-font-tag">
              State
            </p>
            <input
              className="company-add-billing-input px-8 py-4"
              type="text"
              placeholder="State  "
              onChange={(e) => setState(e.target.value)}
              value={state}
              required
            />
          </div>
        </div>
        <div className="flex-row gap-12">
          <div className="flex-column gap-8 flex-1">
            <p style={{ color: '#484848' }} className="xetgo-font-tag">
              Pincode
            </p>
            <input
              className="company-add-billing-input px-8 py-4"
              type="number"
              placeholder="Pincode  "
              onChange={(e) => setPincode(e.target.value)}
              value={pincode}
              required
            />
          </div>

          <div className="flex-column gap-8 flex-1">
            <p style={{ color: '#484848' }} className="xetgo-font-tag">
              Country
            </p>
            <Filter
              option={{
                key: 'country',
                name: selectedCountry.length > 0 ? '' : 'Select Country',
              }}
              optionList={ALL_COUNTRIES || []}
              multipleSelection={false}
              onSelection={(val, _option) => {
                if (val.length > 0) {
                  setSelectedCountry(val);
                } else {
                  setSelectedCountry([]);
                }
              }}
              preSelected={selectedCountry}
              mode="showNames"
              borderStyle="solid"
              showPlaceholder={true}
              direction="left"
              addNew={false}
              classname="company-add-billing-input "
              selectedTitleClass="selected-title"
            />
          </div>
        </div>
        <div className="flex-row gap-12">
          <div className="flex-column gap-8 flex-1">
            <p style={{ color: '#484848' }} className="xetgo-font-tag">
              Type
            </p>
            {update ? (
              <Filter
                option={{
                  key: 'status',
                  name: selectedType.length > 0 ? '' : 'Select Type',
                }}
                optionList={BILLING_TYPE || []}
                multipleSelection={false}
                onSelection={(val, _option) => {
                  if (val.length > 0) {
                    setSelectedType(val);
                  } else {
                    setSelectedType([]);
                  }
                }}
                preSelected={selectedType}
                mode="showNames"
                borderStyle="solid"
                showPlaceholder={true}
                direction="left"
                addNew={false}
                classname="company-add-billing-input "
                selectedTitleClass="selected-title"
              />
            ) : (
              <input
                className="company-add-billing-input px-8 py-4"
                placeholder="Address Type "
                value={
                  addressType.charAt(0).toUpperCase() +
                  addressType.slice(1).toLowerCase()
                }
                required
                disabled
              />
            )}
          </div>

          <div className="flex-column gap-8 flex-1">
            <p style={{ color: '#484848' }} className="xetgo-font-tag">
              Status
            </p>
            <Filter
              option={{
                key: 'country',
                name: selectedStatus.length > 0 ? '' : 'Select Status',
              }}
              optionList={ADDRESS_STATUS || []}
              multipleSelection={false}
              onSelection={(val, _option) => {
                if (val.length > 0) {
                  setSelectedStatus(val);
                } else {
                  setSelectedStatus([]);
                }
              }}
              preSelected={selectedStatus}
              mode="showNames"
              borderStyle="solid"
              showPlaceholder={true}
              direction="left"
              addNew={false}
              classname="company-add-billing-input "
              selectedTitleClass="selected-title"
            />
          </div>
        </div>
        <div className="flex-row gap-12">
          <div className="flex-column gap-8 flex-1">
            <p style={{ color: '#484848' }} className="xetgo-font-tag">
              GSTIN
            </p>
            <input
              type="text"
              className="company-add-billing-input px-8 py-4 xetgo-font-tag"
              placeholder="Enter GSTIN "
              onChange={(e) => setGstin(e.target.value)}
              value={gstin}
              required
            />
          </div>
          <div className="flex-column gap-8 flex-1">
            <p style={{ color: '#484848' }} className="xetgo-font-tag">
              Address Link
            </p>
            <input
              type="text"
              className="company-add-billing-input px-8 py-4 xetgo-font-tag"
              placeholder="Enter Address Link "
              onChange={(e) => setMapLink(e.target.value)}
              value={mapLink}
              required
            />
          </div>
        </div>
      </form>

      <div className="add-product-modal-btns-container justify-content-end flex-row gap-12 new-form-btn-container">
        <div onClick={handleClose} className="cancel-btn xetgo-font-tag">
          Cancel
        </div>
        {update ? (
          <div onClick={handleUpdateAddress} className="add-btn xetgo-font-tag">
            Update
          </div>
        ) : (
          <div onClick={handleAddAddress} className="add-btn xetgo-font-tag">
            Add
          </div>
        )}
      </div>
    </div>
  );
};

export default AddNewCompanyAddress;
